<script setup lang="ts">
import { Network } from '@capacitor/network';

const config = useRuntimeConfig();
const platformStore = usePlatformStore();
const settingsStore = useSettingsStore();
const roleManagerStore = useRoleManagerStore();
const hotkeyManager = useHotkeyManager();
const accountSettingsStore = useAccountSettingsStore();
const notificationStore = useNotificationStore();
const { openToast }=useUserMessages();
const { setLocale, locales, t } = useEkI18n();
const { route } = useRouting();
const { beforeEach } = useRouter();
const { hasUnread } = storeToRefs(notificationStore);
const { availableLocales } = useAvailableLocales();

// Using `_accountSettings` here because using `accountSettings` directly before it has been initialized throws an error
const browserTitle = computed(() => accountSettingsStore._accountSettings?.BrowserTitle);
const tabTitle = computed(() => browserTitle.value ? `App ${browserTitle.value}` : 'App');
const iconPath = computed(() => hasUnread.value ? '/favicon-dot.png' : '/favicon.ico');

useUnhead({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${tabTitle.value} - ${titleChunk}` : tabTitle.value;
  },
  ...(platformStore.isWeb ? { link: [{ rel: 'icon', href: iconPath }] } : {}) // Only update favicon notification dot if on web
});

useI18nToDateFns();
provide(promptBiometricKey, ref(true));
beforeEach((to) => {
  if (roleManagerStore.isRoutePermitted(to.path) === false) {

    openToast(t('toast.noPermissionToPage'), ToastType.ERROR);

    return false;
  }
});

document.body.classList.add(`platform-${platformStore.platform}`);

watch(() => settingsStore.settings.language, async (value) => {
  const selectedLocale = availableLocales.value.find(l => l.code === value);

  if (selectedLocale) {
    await setLocale(selectedLocale.code);
  } else {
    const fallbackLocale = availableLocales.value[0];

    if (fallbackLocale) {
      await settingsStore.set('language', fallbackLocale.code);
      await setLocale(fallbackLocale.code);
    }
  }
}, { immediate: true });

watch(() => settingsStore.settings.theme, (value) => {
  let useDarkmode = false;
  let useHighContrast = false;

  switch (value) {
    case ThemeSetting.system:
      useDarkmode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      useHighContrast = false;
      break;
    case ThemeSetting.light:
      useDarkmode = false;
      useHighContrast = false;
      break;
    case ThemeSetting.dark:
      useDarkmode = true;
      useHighContrast = false;
      break;
    case ThemeSetting.darkHighContrast:
      useDarkmode = true;
      useHighContrast = true;
      break;
  }

  document.body.classList.toggle('dark', useDarkmode);
  document.body.classList.toggle('darkHighContrast', useHighContrast);
}, { immediate: true });

let i = 0;

if (useRuntimeConfig().public.showDevPages === true) {
  window.addEventListener('keypress', (event: KeyboardEvent) => {
    if (event.target instanceof Element && event.target.matches('body') === false) {
      return;
    }

    switch (event.key) {
      case 'å':
      case 'Å':
      {
        i++;
        const a = locales.value[i % locales.value.length].language;

        if (a !== undefined) {
          settingsStore.set('language', a);
        }

        event.preventDefault();
        break;
      }
    }
  });
}

let offlineToast: (HTMLIonToastElement | null) = null;
let connectedToast: (HTMLIonToastElement | null) = null;
Network.addListener('networkStatusChange', async (status) => {
  if (route.name == 'worksheets-worksheetId-qa-schemaId') {
    if (status.connected === false && offlineToast == null) {
      if (connectedToast && await connectedToast.dismiss()) {
        connectedToast.remove();
        connectedToast = null;
      }

      offlineToast = await toastController.create({
        message: t('alert.notConnected'),
        color: 'danger',
        position: 'top',
        buttons: [
          {
            text: 'x',
            role: 'cancel'
          }
        ]
      });
      await offlineToast.present();
    }

    if (status.connected === true) {
      if (offlineToast && await offlineToast.dismiss()) {
        offlineToast.remove();
      }

      if (offlineToast != null) {
        offlineToast = null;
        connectedToast = await toastController.create({
          message: t('alert.reconnected'),
          color: 'success',
          position: 'top',
          duration: 3000
        });
        await connectedToast.present();
      }
    }
  }
});

if (platformStore.isWeb) {
  window.addEventListener('keydown', hotkeyManager.handleKeydown);
}
</script>

<template>
  <DebugOverlay v-if="config.public.showDevPages" />
  <IonApp>
    <LayoutSideMenu v-if="route.path !== '/'" />
    <IonRouterOutlet
      id="main-content"
      :animated="false"
    />
    <LayoutNotificationMenu v-if="accountSettingsStore._accountSettings?.UsePush === true && route.path !== '/'" />
  </IonApp>
</template>

<style scoped>
ion-split-pane {
  --side-width: 16em;
}
</style>
